import { unref } from 'vue';
import { useTraits } from '@common-vue/media-card-large';
import { useInitColorPDP as useInitColor, useColor as useColorMCL } from '@common-vue/media-card-large/src/modules/color';
import { injectLocal, provideLocal } from '@vueuse/core';
import { useProductData } from '@composables/useProductData';
import { useProductType } from '@composables/useProductType';
import { useConsolidatedLatchkey } from '@modules/latchkey/composables';

/**
 * TODO: Traits initialization should be moved to the Product level
 * @description
 * This composable is responsible for providing the color data for the Product.
 * But it also handles complicated case when the Product is a Consolidated Latchkey.
 */
const COLORS_TRAITS_REPOSITORY = 'colors-traits';

/**
 * @param {string} id
 * @returns {{
 *  selectedColorId: SelectedColorId
 *  selectedColor: import('vue').WritableComputedRef<Color | null>
 *  colorProps: import('vue').ComputedRef<ColorProps>
 *  colors: import('vue').ComputedRef<Color[]>
 * }}
 */
function getData(id) {
  const { isConsolidatedLatchKey } = useProductType();
  if (isConsolidatedLatchKey) {
    const {
      colors, colorProps, selectedColorId, selectedColor,
    } = useConsolidatedLatchkey(id);

    return {
      colors,
      colorProps,
      selectedColorId,
      selectedColor,
    };
  }

  const { colors } = useColorMCL(id);
  const product = useProductData(id);
  const colorProps = useInitColor({ id, product: unref(product) });
  const { selectedColorId, selectedColor } = useTraits(id);

  return {
    colors,
    colorProps,
    selectedColorId,
    selectedColor,
  };
}

/**
 * @param {string} id
 * @returns {{
 *  selectedColorId: SelectedColorId
 *  selectedColor: import('vue').WritableComputedRef<Color | null>
 *  colorProps: import('vue').ComputedRef<ColorProps>
 *  colors: import('vue').ComputedRef<Color[]>
 * }}
 */
export default function useColor(id) {
  const resultRepository = `${COLORS_TRAITS_REPOSITORY}-${id}`;
  const providedContext = injectLocal(resultRepository);

  if (providedContext) {
    return providedContext;
  }

  const toProvide = getData(id);
  provideLocal(resultRepository, toProvide);
  return toProvide;
}
